import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { unresolvedExceptions } from "../domain/engine";
import type { ExceptionCase } from "../domain/types";
import { AppShell } from "../components/AppShell";
import { WorkspaceGate } from "../components/WorkspaceGate";
import { businessDateKey } from "../domain/time";

const kindLabel: Record<ExceptionCase["kind"], string> = {
  security: "보안",
  attendance: "출결",
  makeup: "보강",
  guardian: "보호자 안내",
  billing: "수납",
  payroll: "강사 수당",
  support: "지원",
};

export function CloseTodayPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "sample_user"]}>
      <CloseDesk />
    </WorkspaceGate>
  );
}

function CloseDesk() {
  const { state, command, mutating, lastReceipt } = useWorkspace();
  const open = useMemo(() => unresolvedExceptions(state!), [state]);
  const justCreatedException =
    lastReceipt?.eventType === "attendance.mark"
      ? open.find((item) => lastReceipt.changedIds.includes(item.id))
      : undefined;
  const [selectedId, setSelectedId] = useState(
    justCreatedException?.id ?? open[0]?.id ?? null,
  );
  const [deferMode, setDeferMode] = useState(false);
  const [deferReason, setDeferReason] = useState("");
  useEffect(() => {
    if (!selectedId || !open.some((item) => item.id === selectedId))
      setSelectedId(open[0]?.id ?? null);
  }, [open, selectedId]);
  const selected = open.find((item) => item.id === selectedId) ?? open[0];
  const latestClose = state!.closeSnapshots
    .filter((item) => item.period === "day")
    .at(-1);

  async function resolve() {
    if (!selected) return;
    await command({
      type: "exception.resolve",
      exceptionId: selected.id,
      action: "accept_suggestion",
    });
    setDeferMode(false);
  }

  async function defer() {
    if (!selected) return;
    await command({
      type: "exception.defer",
      exceptionId: selected.id,
      reason: deferReason,
    });
    setDeferMode(false);
    setDeferReason("");
  }

  async function closeToday() {
    await command({
      type: "close.complete",
      period: "day",
      periodKey: businessDateKey(new Date(), state!.tenant.timezone),
    });
  }

  return (
    <AppShell
      title="오늘 마침"
      description={
        open.length
          ? `남은 일 ${open.length}건 · 한 건씩 확인하세요`
          : latestClose && !latestClose.reopenedAt
            ? "오늘 운영 마감 기록이 준비됐어요"
            : "남은 일을 모두 확인했어요"
      }
    >
      {open.length > 0 && selected ? (
        <section className="closing-desk" aria-label="오늘 남은 일 처리">
          <aside className="closing-queue">
            <header>
              <p>오늘 남은 일</p>
              <strong>{open.length}</strong>
            </header>
            <ol>
              {open.map((item, index) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={selected.id === item.id ? "is-current" : ""}
                    aria-current={selected.id === item.id ? "step" : undefined}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <small>
                        {kindLabel[item.kind]} ·{" "}
                        {item.severity === "high" ? "먼저 확인" : "확인 필요"}
                      </small>
                      <strong>{item.title}</strong>
                    </div>
                  </button>
                </li>
              ))}
            </ol>
            <footer>
              <span>결정은 변경 내역에 남고 다시 열 수 있어요.</span>
            </footer>
          </aside>

          <article className="exception-sheet" key={selected.id}>
            <header>
              <div>
                <span
                  className={`severity-tag severity-tag--${selected.severity}`}
                >
                  {kindLabel[selected.kind]}
                </span>
                <small>오늘 수업에서 이어진 일</small>
              </div>
              <p>
                기한{" "}
                {new Intl.DateTimeFormat("ko-KR", {
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(new Date(selected.dueAt))}
              </p>
            </header>
            <div className="exception-sheet__body">
              <p className="document-label">처리할 한 건</p>
              <h2>{selected.title}</h2>
              <p className="exception-summary">{selected.summary}</p>
              <dl className="cause-ledger">
                <div>
                  <dt>왜 생겼나요</dt>
                  <dd>{selected.cause}</dd>
                </div>
                <div>
                  <dt>어떤 규칙인가요</dt>
                  <dd>{selected.rule}</dd>
                </div>
                <div>
                  <dt>무엇이 달라지나요</dt>
                  <dd>{selected.impact}</dd>
                </div>
              </dl>
              <div className="suggested-action">
                <span>제안</span>
                <p>{selected.suggestedAction}</p>
              </div>
            </div>
            <footer>
              {deferMode ? (
                <div className="defer-form">
                  <label htmlFor="defer-reason">보류 이유</label>
                  <textarea
                    id="defer-reason"
                    value={deferReason}
                    onChange={(event) => setDeferReason(event.target.value)}
                    placeholder="다음 사람이 이해할 수 있게 적어 주세요."
                  />
                  <div>
                    <button
                      type="button"
                      className="text-action"
                      onClick={() => setDeferMode(false)}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={mutating || deferReason.trim().length < 4}
                      onClick={() => void defer()}
                    >
                      이유와 함께 보류
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="text-action"
                    onClick={() => setDeferMode(true)}
                  >
                    나중에 처리
                  </button>
                  <button
                    type="button"
                    className="primary-action"
                    onClick={() => void resolve()}
                    disabled={mutating}
                  >
                    {mutating
                      ? "저장 확인 중…"
                      : selected.kind === "attendance"
                        ? "보강 후보로 남기고 확인"
                        : "확인하고 이 항목 마침"}
                  </button>
                </>
              )}
            </footer>
          </article>

          <aside className="effect-panel">
            <h2>같이 바뀌는 기록</h2>
            <ol>
              <li className="is-linked">
                <span>원인이 된 기록</span>
                <strong>{kindLabel[selected.kind]} 기록</strong>
              </li>
              <li className={selected.kind === "attendance" ? "is-linked" : ""}>
                <span>수강 횟수</span>
                <strong>
                  {selected.kind === "attendance" ? "차감 없음" : "변경 없음"}
                </strong>
              </li>
              <li className={selected.kind === "attendance" ? "is-linked" : ""}>
                <span>보강 후보</span>
                <strong>
                  {selected.kind === "attendance" ? "1건 준비" : "변경 없음"}
                </strong>
              </li>
              <li className={selected.kind === "attendance" ? "is-linked" : ""}>
                <span>보호자 안내</span>
                <strong>
                  {selected.kind === "attendance"
                    ? "초안 · 미발송"
                    : "변경 없음"}
                </strong>
              </li>
            </ol>
            <div className="provider-boundary">
              <strong>외부로 보내지 않았어요</strong>
              <p>샘플에서는 메시지·결제·수당 송금을 실행하지 않습니다.</p>
            </div>
            {lastReceipt ? (
              <div className="last-receipt">
                <span>마지막 저장</span>
                <strong>{lastReceipt.summary}</strong>
                <small>필요하면 변경 내역에서 되돌릴 수 있어요.</small>
              </div>
            ) : null}
          </aside>
        </section>
      ) : (
        <CloseReceipt
          state={state!}
          latestClose={latestClose}
          onClose={() => void closeToday()}
          mutating={mutating}
          command={command}
        />
      )}
    </AppShell>
  );
}

function CloseReceipt({
  state,
  latestClose,
  onClose,
  mutating,
  command,
}: {
  state: NonNullable<ReturnType<typeof useWorkspace>["state"]>;
  latestClose:
    | NonNullable<
        ReturnType<typeof useWorkspace>["state"]
      >["closeSnapshots"][number]
    | undefined;
  onClose: () => void;
  mutating: boolean;
  command: ReturnType<typeof useWorkspace>["command"];
}) {
  const active =
    latestClose && !latestClose.reopenedAt ? latestClose : undefined;
  if (!active)
    return (
      <section className="ready-to-close">
        <div className="closing-symbol" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </div>
        <p>남은 일 0</p>
        <h2>오늘 기록을 한 번 더 대조했어요.</h2>
        <ul>
          <li>외부 효과 실패 없음</li>
          <li>중요 항목 미해결 0건</li>
          <li>보류 사유 기록 완료</li>
        </ul>
        <button
          className="primary-action primary-action--large"
          type="button"
          onClick={onClose}
          disabled={mutating}
        >
          {mutating ? "마감 기록 만드는 중…" : "오늘 운영 마침"}
        </button>
      </section>
    );
  return (
    <section className="close-receipt">
      <div className="close-receipt__seal">
        <span>완료</span>
        <strong>
          오늘 운영
          <br />
          마침
        </strong>
        <small>변경 차수 {active.revision}</small>
      </div>
      <article>
        <header>
          <p>
            {new Intl.DateTimeFormat("ko-KR", {
              dateStyle: "long",
              timeZone: state.tenant.timezone,
            }).format(new Date(active.completedAt))}
          </p>
          <h2>{state.tenant.name} 오늘 마감 기록</h2>
          <span>
            {new Intl.DateTimeFormat("ko-KR", {
              dateStyle: "medium",
              timeStyle: "short",
              timeZone: state.tenant.timezone,
            }).format(new Date(active.completedAt))}
          </span>
        </header>
        <dl>
          <div>
            <dt>수업</dt>
            <dd>{active.counts.sessions}개</dd>
          </div>
          <div>
            <dt>출결</dt>
            <dd>{active.counts.students}명</dd>
          </div>
          <div>
            <dt>안내 초안</dt>
            <dd>{active.counts.messages}건</dd>
          </div>
          <div>
            <dt>보강 후보</dt>
            <dd>{active.counts.makeups}건</dd>
          </div>
          <div>
            <dt>수납 차이</dt>
            <dd>{active.counts.billingDifferences}건</dd>
          </div>
          <div>
            <dt>보류</dt>
            <dd>{active.counts.deferred}건</dd>
          </div>
        </dl>
        <div className="receipt-truth">
          <strong>이 마감 기록이 뜻하는 것</strong>
          <p>
            오늘 확인할 운영 항목을 모두 처리하거나 이유와 함께 보류했습니다.
            메시지 전송·결제·급여 송금은 각각의 완료 기록이 있을 때만 완료입니다.
          </p>
        </div>
        <footer>
          <button
            type="button"
            className="secondary-action"
            onClick={() =>
              void command({
                type: "close.reopen",
                snapshotId: active.id,
                reason: "샘플 기록을 다시 확인합니다.",
              })
            }
          >
            이 마감 다시 열기
          </button>
          <a className="text-action" href="/api/export" download>
            전체 기록 내보내기
          </a>
        </footer>
      </article>
    </section>
  );
}
